import { Analysis, PossibleRootCauseCategory, ProbabilityCategory } from '.';

/**
 * Stress & Adrenal Balance analysis.
 *
 * First cut (MVP): a single-marker interpretation per marker, mirroring the
 * Electrolytes/Hormones style, plus the adrenal-axis gauge. The markers
 * themselves are MOVED here (display + interpretation):
 *   • Na/K ratio        — from Electrolytes (was 9032/9034)
 *   • Cortisol AM/PM    — from Hormones (was 15000/15010 / 15020/15030)
 *   • DHEA-S            — from Hormones (was 15040/15050)
 *   • Pregnenolone      — from Hormones (was 15220/15230)
 *
 * Possible root-cause CONTENT lives in the FurtherConsiderations DB keyed to the
 * 90xxx categoryIds (same as the thyroid pattern categories), to be populated
 * later. A full physiology-area pattern set (like thyroid's) is a planned
 * follow-up that drops onto this scaffold.
 */

/**
 * Adrenal-axis gauge driven by the Na/K ratio, rendered on the SAME standard
 * 0–100% sweep the Glucose / Thyroid gauges use (LabsmartsGuage).
 *
 * Model — Na/K is the primary direction-setter:
 *   • Na/K below optimal  → adrenal INSUFFICIENCY
 *   • Na/K above optimal  → adrenal EXCESS
 *   • Na/K within optimal → cortisol / DHEA-S set the direction (their strongest
 *       deviation wins); when those are optimal too → BALANCED (Less Likely).
 * Once the direction is fixed, every marker deviating that way contributes to
 * the severity (worst deviation wins); opposite-direction markers are ignored,
 * so the panel always reads a single condition.
 *
 * Severity ladder (mirrors the Na/K reference bands and the Glucose gauge
 * thresholds): within optimal → Less Likely; outside optimal, within lab →
 * Possible; outside lab, within alarm → Likely; outside alarm → Highly Likely.
 */
export type AdrenalCondition = 'insufficiency' | 'excess' | 'balanced';

/** A direction arrow shown on the condition line beneath the gauge. */
export interface AdrenalMarkerArrow {
  direction: 'up' | 'down';
  label: string;
}

export interface AdrenalGauge {
  /** 0–100 needle position for the standard sweep gauge (LabsmartsGuage). */
  probability: number;
  category: ProbabilityCategory;
  /** Direction chosen by the Na/K ratio. */
  condition: AdrenalCondition;
  /** Raw Na/K ratio value (the panel shows the marker's displayValue). */
  ratioValue: number;
  /** Na/K + any same-direction escalators, for the arrows on the line below. */
  markers: AdrenalMarkerArrow[];
}

/** Severity rank → standard-gauge needle position + likelihood word. The needle
 *  values land inside the matching colour band (green < 50 · yellow 50–70 ·
 *  orange 70–90 · red ≥ 90), matching the Glucose gauge thresholds. */
const RANK_TO_GAUGE: Array<{ category: ProbabilityCategory; probability: number }> = [
  { category: ProbabilityCategory.LESS_LIKELY, probability: 25 },
  { category: ProbabilityCategory.POSSIBLE, probability: 60 },
  { category: ProbabilityCategory.LIKELY, probability: 80 },
  { category: ProbabilityCategory.HIGHLY_LIKELY, probability: 95 },
];

/**
 * Severity rank (1 Possible · 2 Likely · 3 Highly Likely) of a marker deviating
 * in `direction`, or null when the marker is unmeasured, range-less, or not
 * deviating that way. Alarm bounds fall back to the lab bounds when undefined,
 * so a range with no alarm thresholds simply tops out at Likely.
 */
function deviationRank(marker: any, direction: 'low' | 'high'): number | null {
  if (!marker?.hasValue?.() || !marker.range) return null;
  const r = marker.range;
  const v = marker.value;
  if (direction === 'low') {
    if (!(v < r.optimalMin)) return null;
    const alarmMin = r.alarmMin ?? r.labMin;
    if (v < alarmMin) return 3;
    if (v < r.labMin) return 2;
    return 1;
  }
  if (!(v > r.optimalMax)) return null;
  const alarmMax = r.alarmMax ?? r.labMax;
  if (v > alarmMax) return 3;
  if (v > r.labMax) return 2;
  return 1;
}

/**
 * Direction the escalator hormones point when Na/K is silent. The strongest
 * deviation across cortisol AM/PM, DHEA-S and pregnenolone wins; ties break
 * toward the higher-priority marker (cortisol AM, then PM, then DHEA-S, then
 * pregnenolone). Null when none of them deviate.
 */
function hormoneDirection(markers: any[]): 'low' | 'high' | null {
  let bestRank = 0;
  let dir: 'low' | 'high' | null = null;
  for (const m of markers) {
    const low = deviationRank(m, 'low');
    const high = deviationRank(m, 'high');
    if (low != null && low > bestRank) {
      bestRank = low;
      dir = 'low';
    } else if (high != null && high > bestRank) {
      bestRank = high;
      dir = 'high';
    }
  }
  return dir;
}

/**
 * Map the adrenal markers to the standard-sweep gauge state. Pure + null-safe so
 * it can be unit-tested and reused by the insights component. Na/K is required
 * (it's the primary direction-setter); returns null when Na/K is unmeasured or
 * range-less.
 */
export function adrenalGauge(
  naK?: any,
  escalators: { cortisolAM?: any; cortisolPM?: any; dheaS?: any; pregnenolone?: any } = {},
): AdrenalGauge | null {
  if (!naK?.hasValue?.() || !naK.range) return null;
  const { optimalMin, optimalMax } = naK.range;
  const v = naK.value;
  const { cortisolAM, cortisolPM, dheaS, pregnenolone } = escalators;

  // ── Direction: Na/K is primary; when it's optimal, the hormones set it ──
  let dir: 'low' | 'high' | null;
  if (v < optimalMin) {
    dir = 'low';
  } else if (v > optimalMax) {
    dir = 'high';
  } else {
    dir = hormoneDirection([cortisolAM, cortisolPM, dheaS, pregnenolone]);
  }

  // Nothing deviates → balanced, adrenal dysfunction Less Likely.
  if (dir === null) {
    return {
      probability: RANK_TO_GAUGE[0].probability,
      category: RANK_TO_GAUGE[0].category,
      condition: 'balanced',
      ratioValue: v,
      markers: [],
    };
  }

  const condition: AdrenalCondition = dir === 'low' ? 'insufficiency' : 'excess';
  const arrow: 'up' | 'down' = dir === 'low' ? 'down' : 'up';

  // Severity = worst deviation among the markers pointing this direction. Na/K
  // leads the marker row; only markers that actually deviate this way appear
  // (so when Na/K is optimal it's absent and the hormones carry the read).
  let rank = 0;
  const markers: AdrenalMarkerArrow[] = [];

  const naKRank = deviationRank(naK, dir);
  if (naKRank != null) {
    rank = Math.max(rank, naKRank);
    markers.push({ direction: arrow, label: 'Na/K' });
  }

  // Cortisol AM / PM collapse to a single "Cortisol" arrow (worst of the two).
  const cortisolRank = Math.max(
    deviationRank(cortisolAM, dir) ?? 0,
    deviationRank(cortisolPM, dir) ?? 0,
  );
  if (cortisolRank > 0) {
    rank = Math.max(rank, cortisolRank);
    markers.push({ direction: arrow, label: 'Cortisol' });
  }

  const dheaRank = deviationRank(dheaS, dir) ?? 0;
  if (dheaRank > 0) {
    rank = Math.max(rank, dheaRank);
    markers.push({ direction: arrow, label: 'DHEA-S' });
  }

  const pregnenoloneRank = deviationRank(pregnenolone, dir) ?? 0;
  if (pregnenoloneRank > 0) {
    rank = Math.max(rank, pregnenoloneRank);
    markers.push({ direction: arrow, label: 'Pregnenolone' });
  }

  const { category, probability } = RANK_TO_GAUGE[rank];
  return { probability, category, condition, ratioValue: v, markers };
}

export class StressAdrenalAnalysis extends Analysis {
  calculateInsights() {
    // Na/K ratio (moved from Electrolytes, was 9032/9034)
    if (this.ba.sodiumPotassiumRatio.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90000);
    } else if (this.ba.sodiumPotassiumRatio.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90010);
    }

    // Sodium out of optimal (either direction) → see Electrolytes section
    if (this.ba.sodium.isNotOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90012);
    }

    // Potassium out of optimal (either direction) → see Electrolytes section
    if (this.ba.potassium.isNotOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90014);
    }

    // Cortisol AM (moved from Hormones, was 15000/15010)
    if (this.ba.cortisolAM.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90015);
    } else if (this.ba.cortisolAM.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90020);
    }

    // Cortisol PM (moved from Hormones, was 15020/15030)
    if (this.ba.cortisolPM.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90025);
    } else if (this.ba.cortisolPM.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90030);
    }

    // DHEA-S (moved from Hormones, was 15040/15050)
    if (this.ba.dheaS.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90035);
    } else if (this.ba.dheaS.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90040);
    }

    // Pregnenolone (moved from Hormones, was 15220/15230)
    if (this.ba.pregnenolone.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90080);
    } else if (this.ba.pregnenolone.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90090);
    }
  }

  /** Standard-sweep adrenal-axis gauge: Na/K picks the direction, cortisol and
   *  DHEA-S escalate the severity. Null when the Na/K ratio is unmeasured. */
  adrenalGaugeState(): AdrenalGauge | null {
    return adrenalGauge(this.ba.sodiumPotassiumRatio, {
      cortisolAM: this.ba.cortisolAM,
      cortisolPM: this.ba.cortisolPM,
      dheaS: this.ba.dheaS,
      pregnenolone: this.ba.pregnenolone,
    });
  }
}
